/**
 * @file ReprendreSportApresOpBas.tsx
 * @summary Article de blog — ReprendreSportApresOpBas
 *
 * Page d'article TSX du silo SEO/GEO. Inclut métadonnées (SEO), JSON-LD, et CTA Resawod. Inscriptions cours via Resawod uniquement (sauf séances d'essai).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import { Link } from "react-router-dom";
import ArticleLayout, {
  EnBref, QuandConsulter, AdaptationTable, FAQList, FurtherReading, SourcesSection, buildArticleJsonLd,
} from "@/components/blog/BlogParts";

const URL = "https://crossfitchistera.fr/blog/reprendre-sport-apres-operation-bas-du-corps";
const TITLE = "Reprendre le sport après une opération du bas du corps";
const DESC = "Genou, hanche, cheville : étapes, critères et adaptations concrètes pour reprendre le sport après une chirurgie du bas du corps.";

const author = { name: "Anthony Bendahmane (Benbou)", role: "Master STAPS, CrossFit Level 1" };
const reviewer = { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" };

const faq = [
  { q: "Combien de temps après l'opération ?", a: "Très variable : 6 à 9 mois pour un LCA, 3 à 4 mois pour un ménisque, 3 à 6 mois pour une prothèse de hanche, 2 à 4 mois pour une cheville selon le geste. Dépend des feux verts médicaux." },
  { q: "Puis-je faire du cardio avant la reprise complète ?", a: "Oui : vélo, rameur sans appuis violents, natation selon op. C'est même recommandé pour entretenir la condition." },
  { q: "Comment évaluer si je peux courir ?", a: "Tests classiques : single leg squat sans douleur, hop tests symétriques à 90% du côté opposé, force quadriceps proche du côté sain." },
  { q: "Je sens des craquements, c'est grave ?", a: "Souvent bénin. Si pas de douleur, pas d'instabilité, pas de gonflement : on continue. Sinon, retour kiné." },
];

export default function ReprendreSportApresOpBas() {
  return (
    <ArticleLayout
      url={URL} title={TITLE} description={DESC}
      h1="Reprendre le sport après une opération du bas du corps"
      breadcrumbLabel="Op. bas du corps"
      category="Reprise · Post-opératoire"
      author={author} reviewer={reviewer}
      publishedAt="2026-12-18" readingMinutes={11}
      ctaVariant="reprise" needsHealthDisclaimer
      jsonLd={buildArticleJsonLd({ url: URL, title: TITLE, description: DESC, publishedAt: "2026-12-18", author, reviewer, faq })}
    >
      <EnBref>
        <p>Genou, hanche, cheville : la reprise sportive après chirurgie du bas du corps est un <strong>processus en plusieurs mois</strong>. Sauter la phase de réathlétisation, c'est multiplier le risque de récidive. La bonne stratégie : critères mesurables, adaptations claires, et continuité du haut du corps pendant la convalescence.</p>
      </EnBref>

      <QuandConsulter title="Validations indispensables avant de reprendre">
        <ul>
          <li>Feu vert chirurgien et kiné, par écrit si possible</li>
          <li>Force quadriceps/ischios mesurée (pour le genou) supérieure à 80% du côté sain</li>
          <li>Hop tests ou équivalents symétriques</li>
          <li>Aucune douleur, aucun gonflement post-effort en fin de rééducation</li>
        </ul>
      </QuandConsulter>

      <h2>Cas spécifiques</h2>
      <h3>Genou (LCA, ménisque, prothèse)</h3>
      <p>Voir notre article dédié <Link to="/blog/reprendre-sport-apres-ligament-croise">"Reprendre le sport après un ligament croisé"</Link> pour le LCA. Pour la prothèse, l'objectif n'est pas la performance mais la longévité de l'implant : on évite l'impact prolongé.</p>
      <h3>Hanche (prothèse, conflit, arthroscopie)</h3>
      <p>Après prothèse totale, certains mouvements sont à éviter à vie selon la voie d'abord (flexion forcée + rotation). Voir <Link to="/blog/douleur-hanche-retour-sport">"Douleur à la hanche et retour au sport"</Link>.</p>
      <h3>Cheville (entorse opérée, fracture, arthroscopie)</h3>
      <p>Le travail proprioceptif est central. Pas de course ni de saut tant que l'appui monopodal yeux fermés n'est pas stable 30 secondes.</p>

      <h2>Adaptations pendant la reprise</h2>
      <AdaptationTable rows={[
        { standard: "Back squat, front squat", adapted: "Goblet squat amplitude limitée, leg press" },
        { standard: "Box jumps, double-unders", adapted: "Step-ups, single-unders sur tapis souple" },
        { standard: "Course (run 400 m, 800 m)", adapted: "Rameur, vélo (selon op), marche rapide" },
        { standard: "Burpees", adapted: "Burpees sans saut (step back), au sol contrôlés" },
        { standard: "Cleans, snatchs en pleine amplitude", adapted: "Hang cleans, hang snatchs, technique légère" },
        { standard: "Wall balls, thrusters", adapted: "Front squat à amplitude réduite + push press" },
      ]} />

      <h2>Notre approche à la box</h2>
      <p>Le programme <Link to="/reprendre-le-sport">Reprendre le sport</Link> est pensé pour ces situations. Vous démarrez avec un échange dédié avec un coach, vos restrictions sont notées, et chaque WOD est adapté en temps réel. Quand vous êtes prêt, on vous bascule progressivement sur les classes WOD classiques, avec encore quelques adaptations si besoin.</p>

      <FAQList items={faq} />

      <FurtherReading items={[
        { label: "Reprendre le sport après un ligament croisé", to: "/blog/reprendre-sport-apres-ligament-croise" },
        { label: "Douleur à la hanche et retour au sport", to: "/blog/douleur-hanche-retour-sport" },
        { label: "Douleur au genou en CrossFit", to: "/blog/douleur-genou-crossfit" },
        { label: "Reprendre le sport après une blessure (pilier)", to: "/blog/reprendre-le-sport-apres-blessure" },
      ]} />

      <SourcesSection items={[
        { label: "BJSM — Return to sport criteria after ACL reconstruction", href: "https://bjsm.bmj.com/content/50/13/804" },
        { label: "HAS — Rééducation après prothèse de hanche", href: "https://www.has-sante.fr/" },
      ]} />
    </ArticleLayout>
  );
}
